import { Head, Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { companiesPath } from '@/lib/routes'

import {
  CompanyFormFields,
  type BatchOption,
  type CompanyFormData,
  type CountryOption,
  type IndustryOption,
} from './_Form'

type CompaniesNewProps = {
  batches: BatchOption[]
  countries: CountryOption[]
  industries: IndustryOption[]
}

function CompaniesNew({ batches, countries, industries }: CompaniesNewProps) {
  const form = useForm<CompanyFormData>({
    name: '',
    legal_name: '',
    oneliner_en: '',
    oneliner_es: '',
    description_en: '',
    description_es: '',
    webpage: '',
    linkedin: '',
    country: '',
    logo: null,
    remove_logo: false,
    industry_ids: [],
    founded_year: '',
    status: 'operating',
    batch_id: null,
    female_founder: false,
  })

  return (
    <>
      <Head title="New company" />

      <div className="mx-auto max-w-3xl px-8 py-10">
        <Link
          href={companiesPath()}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to companies
        </Link>

        <PageHeader
          title="New company"
          description="Create a portfolio entry. Further details (events, valuations, founders) happen after this."
        />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ company: data }))
            form.post(companiesPath(), { forceFormData: true })
          }}
        >
          <CompanyFormFields
            data={form.data}
            errors={form.errors as Partial<Record<keyof CompanyFormData, string>>}
            setData={form.setData}
            batches={batches}
            countries={countries}
            industries={industries}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Link href={companiesPath()}>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Create company'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

export default Object.assign(CompaniesNew, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
